import { useCallback, useRef, useState } from 'react';
import { useInlineRowEdit } from '@shared/hooks/useInlineRowEdit';
import { getFieldErrors, isApiError } from '@/services/api';
import { sageNominalSchema } from '../schemas/sageNominal.schema';
import type { SageNominal } from '../types/dealerSageMappings.types';
import { todayIsoDate } from '../utils/todayIsoDate';
import { useDealerSageMappingMutation } from './useDealerSageMappingMutation';

export interface SageRowTarget {
    productId: string;
    nominal: SageNominal | null;
    label: string;
}

export interface SageRowDrafts {
    code: React.MutableRefObject<string>;
    name: React.MutableRefObject<string>;
    date: React.MutableRefObject<string>;
}

const SAVE_FAILURE = 'The Sage account code could not be saved.';
const REMOVE_FAILURE = 'The Sage account code could not be removed.';

const saveFailureMessage = (error: unknown): string => {
    if (isApiError(error) && error.status === 422) {
        const first = Object.values(getFieldErrors(error))[0]?.[0];
        if (first) return first;
    }
    return SAVE_FAILURE;
};

export function useSageMappingRowEditor(dealerId: string) {
    const mutation = useDealerSageMappingMutation(dealerId);
    const inlineEdit = useInlineRowEdit<string>();
    const [error, setError] = useState<string | null>(null);
    const [pendingRemoval, setPendingRemoval] = useState<SageRowTarget | null>(null);
    const [isRemoving, setIsRemoving] = useState(false);
    const editingTarget = useRef<SageRowTarget | null>(null);
    const codeDraft = useRef('');
    const nameDraft = useRef('');
    const dateDraft = useRef('');
    const drafts: SageRowDrafts = { code: codeDraft, name: nameDraft, date: dateDraft };
    const { beginEdit: openRow, cancelEdit: closeRow, commit } = inlineEdit;
    const { mutateAsync } = mutation;

    const beginEdit = useCallback((target: SageRowTarget) => {
        codeDraft.current = target.nominal?.nominalCode ?? '';
        nameDraft.current = target.nominal?.nominalName ?? '';
        dateDraft.current = target.nominal?.effectiveFrom ?? todayIsoDate();
        editingTarget.current = target;
        setError(null);
        openRow(target.productId);
    }, [openRow]);

    const cancelEdit = useCallback(() => {
        editingTarget.current = null;
        setError(null);
        closeRow();
    }, [closeRow]);

    const save = useCallback(async () => {
        const target = editingTarget.current;
        if (!target) return;
        const parsed = sageNominalSchema.safeParse({
            nominalCode: codeDraft.current,
            nominalName: nameDraft.current,
            effectiveFrom: dateDraft.current,
        });
        if (!parsed.success) {
            setError(parsed.error.issues[0]?.message ?? SAVE_FAILURE);
            return;
        }
        setError(null);
        try {
            await commit(() => mutateAsync({ productId: target.productId, nominal: parsed.data }).then(() => undefined));
            editingTarget.current = null;
        } catch (failure) {
            setError(saveFailureMessage(failure));
        }
    }, [commit, mutateAsync]);

    const requestRemoval = useCallback((target: SageRowTarget) => {
        setError(null);
        setPendingRemoval(target);
    }, []);

    const cancelRemoval = useCallback(() => setPendingRemoval(null), []);

    const confirmRemoval = useCallback(async () => {
        if (!pendingRemoval) return;
        setIsRemoving(true);
        try {
            await mutateAsync({ productId: pendingRemoval.productId, nominal: null });
        } catch {
            setError(REMOVE_FAILURE);
        } finally {
            setIsRemoving(false);
            setPendingRemoval(null);
        }
    }, [pendingRemoval, mutateAsync]);

    return {
        editingKey: inlineEdit.editingId,
        isSaving: inlineEdit.isPending,
        drafts,
        error,
        beginEdit,
        cancelEdit,
        save,
        pendingRemoval,
        isRemoving,
        requestRemoval,
        cancelRemoval,
        confirmRemoval,
    };
}

export type SageMappingRowEditor = ReturnType<typeof useSageMappingRowEditor>;
